import { Box, Flex, Text } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';
import { ArrowLeftRight } from 'lucide-react';

import { Spinner } from '../spinner/spinner';

export interface SidebarOrgSelectorProps {
  /** Organization name to display. */
  name: string;
  /** Click handler (e.g. open org-switch modal). */
  onClick?: () => void;
  /** Whether the sidebar is collapsed. */
  collapsed?: boolean;
  /** Whether the selector is in a loading state. */
  loading?: boolean;
  /** Additional trailing content or icon override. */
  trailingIcon?: ReactNode;
}

/**
 * KDS SidebarOrgSelector — presentational trigger for org/partner switching.
 *
 * Renders the org name with a swap icon in the sidebar footer style.
 * All modal/switching logic stays in the consuming app.
 * Supports ref forwarding for use with `asChild` composition (e.g. Modal.Trigger).
 */
export const SidebarOrgSelector = forwardRef<HTMLButtonElement, SidebarOrgSelectorProps>(
  ({ name, onClick, collapsed = false, loading = false, trailingIcon, ...rest }, ref) => {
    return (
      <Box
        ref={ref}
        as="button"
        display="flex"
        alignItems="center"
        justifyContent={collapsed || loading ? 'center' : 'space-between'}
        gap={2.5}
        w="100%"
        px={3}
        py={2}
        borderRadius="sm"
        bg="transparent"
        color="neutral.0"
        cursor={loading ? 'default' : 'pointer'}
        border="1px solid"
        borderColor="brand.500"
        transition="background 0.15s"
        css={loading ? {} : {
          _hover: { bg: 'brand.600' },
        }}
        onClick={loading ? undefined : onClick}
        data-testid="sidebar-org-selector"
        {...rest}
      >
        {loading ? (
          <Spinner size="sm" />
        ) : (
          <>
            {!collapsed && (
              <Text lineClamp={1} color="neutral.0" title={name}>
                {name}
              </Text>
            )}
            <Flex flexShrink={0} boxSize={5} alignItems="center" justifyContent="center">
              {trailingIcon ?? <ArrowLeftRight size={16} />}
            </Flex>
          </>
        )}
      </Box>
    );
  }
);

SidebarOrgSelector.displayName = 'SidebarOrgSelector';
